import React, { useRef, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import '../../styles/ResultScreen.css';

function ResultScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const { images = [] } = location.state || {}; // Safely destructure with default empty array
  const canvasRef = useRef(null);

  const handleDownload = () => {
  const canvas = canvasRef.current;
  if (!canvas) return;

  const link = document.createElement('a');
  link.download = 'photostrip.png';
  link.href = canvas.toDataURL('image/png');
  link.click();
};

  // Implementation with proper image placement
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions based on frame aspect ratio (2400 x 3561)
    const frameAspectRatio = 3561 / 2400;
    canvas.width = 900;
    canvas.height = Math.round(canvas.width * frameAspectRatio);
    
    // Clear canvas with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Calculate photo dimensions based on frame size
    // Frame is 2400 x 3561px, photos are 1043 x 1430px
    const photoScale = 1043 / 2400; // Ratio of photo width to frame width
    
    // Calculate photo placement within the frame
    const photoWidth = canvas.width * photoScale;
    const photoHeight = photoWidth * (1430 / 1043); // Maintain photo aspect ratio
    
    // Define photo positions (percentages of frame dimensions)
    // These values should be adjusted based on your specific frame design
    const photoPositions = [
      { x: 0.26, y: 0.26 },  // Top-left
      { x: 0.73, y: 0.26 },  // Top-right
      { x: 0.26, y: 0.68 },  // Bottom-left
      { x: 0.73, y: 0.68 }   // Bottom-right
    ];
    
    // IMPORTANT: Load frame first to ensure it exists
    const frameImg = new Image();
    // Try multiple potential paths for the frame image
    frameImg.src = '/img/LoveFrame.svg';
    
    // Handle frame loading error by trying alternative paths
    frameImg.onerror = () => {
      console.log('Frame image failed to load at /img/LoveFrame.svg, trying alternative path');
      frameImg.src = 'img/LoveFrame.svg'; // Try without leading slash
      
      frameImg.onerror = () => {
        console.log('Frame image failed to load at img/LoveFrame.svg, trying another path');
        frameImg.src = '../img/LoveFrame.svg'; // Try relative path
        
        frameImg.onerror = () => {
          console.log('All frame image paths failed, drawing default frame');
          drawDefaultFrame();
          // Now draw photos on top of default frame
          drawPhotos();
        };
      };
    };
    
    // Draw default frame if needed
    const drawDefaultFrame = () => {
      // Draw default frame border
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 20;
      ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);
      
      // Add decorative elements
      ctx.fillStyle = '#ff6b6b';
      ctx.font = 'bold 48px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Photo Booth', canvas.width/2, 80);
    };
    
    // Helper function to calculate source crop dimensions for an image to fill target area
    const calculateCropDimensions = (imgWidth, imgHeight, targetWidth, targetHeight) => {
      const imgRatio = imgHeight / imgWidth;
      const targetRatio = targetHeight / targetWidth;
      let sourceX, sourceY, sourceWidth, sourceHeight;
      if (imgRatio > targetRatio) {
        // Image is taller than target - crop top and bottom
        sourceWidth = imgWidth;
        sourceHeight = imgWidth * targetRatio;
        sourceX = 0;
        sourceY = (imgHeight - sourceHeight) / 2;
      } else {
        // Image is wider than target - crop left and right
        sourceHeight = imgHeight;
        sourceWidth = imgHeight / targetRatio;
        sourceX = (imgWidth - sourceWidth) / 2;
        sourceY = 0;
      }
      return { sourceX, sourceY, sourceWidth, sourceHeight };
    };

    // Draw all photos
    const drawPhotos = () => {
      if (!images || images.length === 0) return;
      for (let i = 0; i < Math.min(images.length, 4); i++) {
        const imgSrc = images[i];
        if (!imgSrc) continue;
        const img = new Image();
        img.src = imgSrc;
        img.onload = () => {
          const pos = photoPositions[i];
          const centerX = canvas.width * pos.x;
          const centerY = canvas.height * pos.y;
          const destX = centerX - photoWidth / 2;
          const destY = centerY - photoHeight / 2;
          // Calculate crop dimensions to maintain aspect ratio
          const { sourceX, sourceY, sourceWidth, sourceHeight } = calculateCropDimensions(
            img.naturalWidth,
            img.naturalHeight,
            photoWidth,
            photoHeight
          );
          ctx.drawImage(
            img,
            sourceX, sourceY, sourceWidth, sourceHeight, // Crop from source
            destX, destY, photoWidth, photoHeight        // Draw to destination
          );
          if (frameImg.complete && frameImg.naturalWidth !== 0) {
            ctx.drawImage(frameImg, 0, 0, canvas.width, canvas.height);
          }
        };
        img.onerror = () => {
          console.log(`Photo ${i + 1} failed to load`);
          const pos = photoPositions[i];
          
          // Draw placeholder for failed image
          const centerX = canvas.width * pos.x;
          const centerY = canvas.height * pos.y;
          
          ctx.fillStyle = '#f0f0f0';
          ctx.fillRect(
            centerX - photoWidth / 2,
            centerY - photoHeight / 2,
            photoWidth,
            photoHeight
          );
          
          ctx.fillStyle = '#999';
          ctx.font = '24px sans-serif';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('Photo', centerX, centerY);
          
          // Redraw frame on top
          if (frameImg.complete && frameImg.naturalWidth !== 0) {
            ctx.drawImage(frameImg, 0, 0, canvas.width, canvas.height);
          }
        };
      }
    };
    
    // When frame successfully loads, draw it and then draw photos
    frameImg.onload = () => {
      console.log('Frame image loaded successfully');
      
      // First draw photos
      drawPhotos();
      
      // Then draw the frame on top of the photos (in case photos haven't loaded yet)
      // Set a timeout to ensure the frame gets drawn last
      setTimeout(() => {
        ctx.drawImage(frameImg, 0, 0, canvas.width, canvas.height);
      }, 100);
    };
    
    // If there are no photos, just draw the frame
    if (!images || images.length === 0) {
      frameImg.onload = () => {
        ctx.drawImage(frameImg, 0, 0, canvas.width, canvas.height);
      };
      
      frameImg.onerror = () => {
        drawDefaultFrame();
      };
    }
  }, [images]);

  return (
  <div className="ResultScreen">      
    <div className="content-container">
      <div className="photo-frame-container">
        {images && images.length > 0 ? (
          <canvas 
            ref={canvasRef} 
            className="photo-strip"
            style={{
              width: '100%',
              height: 'auto',
              border: '1px solid #ddd',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
            }}
          />
        ) : (
          <p className="no-photos">No photos taken. Please try again.</p>
        )}
      </div>

      <div className="right-panel">
        <button onClick={handleDownload} className="download-button">
          <svg
          xmlns="http://www.w3.org/2000/svg"
          height="20"
          viewBox="0 0 24 24"
          width="20"
          fill="white"
          style={{ marginRight: '8px' }}
        >
          <path d="M5 20h14v-2H5v2zM12 2v12l4-4h-3V2h-2v8H8l4 4z" />
        </svg>
          Download Photostrip
        </button>

        <div class="hashtag-container">
          <p>Thanks for trying out this frame!</p>
          <p>Don't forget to share using <strong>#moa4cuts</strong> ❤️</p>
        </div>

        <iframe 
          width="100%" 
          height="315" 
          src="https://www.youtube.com/embed/8aRTMQvbODs?si=R9aHX9Y7GuHFDZDa" 
          title="YouTube video player" 
          frameBorder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
          referrerPolicy="strict-origin-when-cross-origin" 
          allowFullScreen
        ></iframe>

        <Link to="/" style={{ color: 'black', padding: '50px 12px' }}>
          🏠 Back to Home
        </Link>
      </div>
    </div>
  </div>
);

}

export default ResultScreen;